import { useCallback, useEffect, useRef, useState } from "react";

import { delay } from "lodash/fp";

import { waitForScrollEnd } from "@app/analytics/utils";

import useScrollAutoSelect, {
  SCROLL_ID_ATTRIBUTE,
  ScrollAutoSelectOptions,
} from "./useScrollAutoSelect";

export interface UseSectionScrollArgs<T extends string> {
  autoSelectThreshold: ScrollAutoSelectOptions<T>["threshold"];
  defaultSection: T;
  enabled?: boolean;
  /** Delay on initial scroll if the selected section is different than the default section */
  initialScrollDelay?: number;
  onSectionChange?: (section: T) => void;
  preselectedSection?: T;
  scrollContainer: ScrollAutoSelectOptions<T>["scrollContainer"];
  sections: ScrollAutoSelectOptions<T>["elements"];
}

const useSectionScroll = <T extends string>(
  args: UseSectionScrollArgs<T>
): [T, (section: T, scrollToSection?: boolean) => void] => {
  const {
    autoSelectThreshold,
    defaultSection,
    initialScrollDelay = 0,
    preselectedSection,
    enabled = true,
    onSectionChange,
    scrollContainer,
    sections,
  } = args;

  const hasInitialScroll =
    preselectedSection && preselectedSection !== defaultSection;

  const [scrolling, setScrolling] = useState(hasInitialScroll);
  const [selectedSection, setSelectedSection] = useState<T>(
    preselectedSection ?? defaultSection
  );
  const performedInitialScroll = useRef<boolean>(!hasInitialScroll);

  const scrollIntoView = useCallback(
    (section: T) => {
      const element = document.querySelector(
        `[${SCROLL_ID_ATTRIBUTE}="${section}"]`
      );

      if (enabled) {
        setScrolling(true);
      }
      onSectionChange?.(section);

      if (!enabled) return;
      element?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      waitForScrollEnd(scrollContainer.current).then(() => {
        setScrolling(false);
      });
    },
    [scrollContainer.current, enabled]
  );

  const handleSectionSelect = useCallback(
    (section: T, scrollToSection = false) => {
      setSelectedSection(section);

      if (scrollToSection) {
        scrollIntoView(section);
      } else {
        onSectionChange?.(section);
      }
    },
    [scrollIntoView]
  );

  useEffect(() => {
    if (!enabled) return;

    if (hasInitialScroll && !performedInitialScroll.current) {
      // force scroll to on initial page load
      delay(initialScrollDelay, () => {
        handleSectionSelect(preselectedSection, true);
        performedInitialScroll.current = true;
      });
    }
  }, [enabled, hasInitialScroll, initialScrollDelay]);

  useScrollAutoSelect<T>({
    elements: sections,
    enabled: enabled && !scrolling,
    scrollContainer,
    threshold: autoSelectThreshold,
    onCrossThreshold: handleSectionSelect,
  });

  return [selectedSection, handleSectionSelect];
};

export default useSectionScroll;
